import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { passwordChecks, validatePasswordPolicy } from "@/lib/password_policy";

function PolicyCheck({ ok, children }) {
  const Icon = ok ? CheckCircle2 : Circle;
  return (
    <li className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-dapnow-muted"}`}>
      <Icon className="h-3.5 w-3.5" /> {children}
    </li>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const checks = useMemo(() => passwordChecks(pw), [pw]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Token di reset mancante o non valido.");
      return;
    }
    const policyErr = validatePasswordPolicy(pw);
    if (policyErr) { toast.error(policyErr); return; }
    if (pw !== confirm) { toast.error("Le due password non coincidono."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password aggiornata. Ora puoi accedere.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-white">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center text-sm text-dapnow-muted hover:text-dapnow-ink mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> Torna al login
        </Link>
        <Logo />
        <p className="overline mt-6">Reset password</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Imposta nuova password</h1>
        <p className="text-sm text-dapnow-muted mt-2">Scegli una nuova password sicura per il tuo account.</p>

        {!token && (
          <p className="mt-4 text-sm text-dapnow-red" data-testid="reset-missing-token">
            Token mancante. Apri il link che hai ricevuto via email.
          </p>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-semibold tracking-tight">Nuova password</label>
            <input
              type="password"
              required
              data-testid="reset-password-input"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="input-tall mt-1.5 w-full"
              placeholder="••••••••"
              autoComplete="new-password"
            />
            <ul className="mt-3 space-y-1" data-testid="password-policy-hints">
              <PolicyCheck ok={checks.length}>almeno 8 caratteri</PolicyCheck>
              <PolicyCheck ok={checks.letter}>almeno una lettera</PolicyCheck>
              <PolicyCheck ok={checks.digit}>almeno un numero</PolicyCheck>
              <PolicyCheck ok={checks.symbol}>almeno un simbolo</PolicyCheck>
            </ul>
          </div>
          <div>
            <label className="text-sm font-semibold tracking-tight">Conferma password</label>
            <input
              type="password"
              required
              data-testid="reset-password-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input-tall mt-1.5 w-full"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !token}
            data-testid="reset-submit-btn"
            className="btn-primary w-full"
          >
            {submitting ? "Aggiornamento…" : "Imposta nuova password"}
          </button>
        </form>
      </div>
    </div>
  );
}
